import './App.css';
import { useEffect, useState, useRef } from 'react';
import React from 'react';
import arrow from './assets/send1.svg';
import sparkles from './assets/stars.svg';
import bg from './assets/bg.jpg';
import Select from 'react-select';
import { v4 as uuidv4 } from 'uuid';
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { languages, languageQuestions, languageCodes } from './constants';

// Levenshtein distance function
function levenshteinDistance(a, b) {
  const matrix = [];

  // increment along the first column of each row
  let i;
  for (i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  // increment each column in the first row
  let j;
  for (j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (i = 1; i <= b.length; i++) {
    for (j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

// Function to detect yes/no responses using Levenshtein distance
function detectYesNo(userInput) {
  const input = userInput.toLowerCase().trim();

  // Common yes/no variations
  const yesWords = ['yes', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'y'];
  const noWords = ['no', 'nah', 'nope', 'n'];

  let minYesDistance = Infinity;
  let minNoDistance = Infinity;

  // Calculate minimum distance to yes words
  yesWords.forEach(word => {
    const distance = levenshteinDistance(input, word);
    minYesDistance = Math.min(minYesDistance, distance);
  });

  // Calculate minimum distance to no words
  noWords.forEach(word => {
    const distance = levenshteinDistance(input, word);
    minNoDistance = Math.min(minNoDistance, distance);
  });

  // Threshold for accepting a match (allowing for 1-2 character differences)
  const threshold = 2;

  if (minYesDistance <= threshold && minYesDistance < minNoDistance) {
    return 'yes';
  } else if (minNoDistance <= threshold && minNoDistance < minYesDistance) {
    return 'no';
  }

  return null; // Neither yes nor no
}

// Thumb Icons Components
const ThumbUpIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
  </svg>
);

const ThumbDownIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
    <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
  </svg>
);

function Chatbot() {
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      msg: "👋 Hello! I'm an AI chatbot capable of addressing your inquiries. Feel free to ask any questions you may have!",
      category: null,
      fromUser: false,
      responseId: null,
      reaction: null
    }
  ]);
  const [chatbotIntro, setChatbotIntro] = useState("Ask anything, and our AI chatbot will do it's best to answer your question.");
  const [chatbotDisclaimer, setChatbotDisclaimer] = useState("This feature utilizes AI, and the information provided may not be fully accurate. We recommend verifying it independently.");
  const [isMicActive, setIsMicActive] = useState(false);
  const [enableMic, setEnableMic] = useState(false);
  const [showFullLanguageText, setShowFullLanguageText] = useState(false);
  const [autoDetectLanguage, setAutoDetectLanguage] = useState(false);
  const [waitingForRephraseResponse, setWaitingForRephraseResponse] = useState(null);
  const [chatbotMessageIcon, setChatbotMessageIcon] = useState(null);
  const [configError, setConfigError] = useState(null);

  // Add state to track rephrased responses
  const [rephrasedResponseIds, setRephrasedResponseIds] = useState(new Set());

  const extractErrorMessage = async (response) => {
    const fallback = `Unable to load chatbot (error ${response.status}). Please try again later.`;
    try {
      const data = await response.json();
      if (typeof data === "string" && data.trim()) return data;
      const detail = data?.detail ?? data?.message ?? data?.error ?? data?.msg;
      if (detail == null || detail === "") return fallback;
      if (typeof detail === "string") return detail;
      try {
        return JSON.stringify(detail);
      } catch {
        return fallback;
      }
    } catch {
      return fallback;
    }
  };

  const handleConfigLoadError = (message) => {
    const errorMessage =
      message || "Unable to load chatbot configuration. Please try again later.";
    setConfigError(errorMessage);
    setBotName((prev) => prev || "Revize");
    setMessages([]);
    setChatbotIntro("This chatbot is temporarily unavailable.");
    setChatbotDisclaimer(errorMessage);
    setEnableMic(false);
  };

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isUsingMediaRecorder, setIsUsingMediaRecorder] = useState(false);

  const themes = {
    primaryColor: "#4900ff",
    blackColor: "#000000",
    secondryColor: "#475569",
    primaryFontColor: "white",
    secondryFontColor: "#2C3333",
    mainBgColor: "#ffffff",
    logoColor: "#ffffff",
    backgroudImage: bg
  };

  const [banner, setBanner] = useState('');
  const [botName, setBotName] = useState('');
  const [categories, setCategories] = useState([{ value: '', label: "Select Category" }]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sessionId, setSessionId] = useState('');
  const [userId, setUserId] = useState("");
  const [timeZone, setTimeZone] = useState(null);
  const selectedLanguageRef = useRef("English");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const urlParams = new URLSearchParams(window.location.search);
  const layout = urlParams.get("layout");

  async function convertToWavBlob(blob) {
    const arrayBuffer = await blob.arrayBuffer();
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const length = audioBuffer.length * numChannels * 2;
    const wavBuffer = new ArrayBuffer(44 + length);
    const view = new DataView(wavBuffer);

    function writeString(view, offset, str) {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    }

    let offset = 0;
    writeString(view, offset, 'RIFF'); offset += 4;
    view.setUint32(offset, 36 + length, true); offset += 4;
    writeString(view, offset, 'WAVE'); offset += 4;
    writeString(view, offset, 'fmt '); offset += 4;
    view.setUint32(offset, 16, true); offset += 4;
    view.setUint16(offset, 1, true); offset += 2;
    view.setUint16(offset, numChannels, true); offset += 2;
    view.setUint32(offset, sampleRate, true); offset += 4;
    view.setUint32(offset, sampleRate * numChannels * 2, true); offset += 4;
    view.setUint16(offset, numChannels * 2, true); offset += 2;
    view.setUint16(offset, 16, true); offset += 2;
    writeString(view, offset, 'data'); offset += 4;
    view.setUint32(offset, length, true); offset += 4;

    for (let i = 0; i < audioBuffer.length; i++) {
      for (let channel = 0; channel < numChannels; channel++) {
        const sample = audioBuffer.getChannelData(channel)[i];
        const s = Math.max(-1, Math.min(1, sample));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        offset += 2;
      }
    }
    return new Blob([view], { type: 'audio/wav' });
  }

  useEffect(() => {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem('sessionId', sessionId);
    }
    setSessionId(sessionId);
  }, []);

  useEffect(() => {
    const loadFingerprint = async () => {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      setUserId(result.visitorId);
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimeZone(userTimeZone);
    };
    loadFingerprint();
  }, []);

  const handleTypeSelect = e => {
    setSelectedCategory(e.value);
  };

  let loaded = false;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let websiteRef = urlParams.get("websiteRef");

    if (websiteRef != null && websiteRef.trim() !== "" && !loaded) {
      loaded = true;

      const defaultWelcome =
        "👋 Hello! I'm an AI chatbot capable of addressing your inquiries. Feel free to ask any questions you may have!";

      // Critical APIs only: config + site. Other failures keep chat open.
      fetch(`${process.env.REACT_APP_SERVER_URL}/api/website/get_bot_configurations/${websiteRef}`)
        .then(async (response) => {
          if (!response.ok) {
            const msg = await extractErrorMessage(response);
            const err = new Error(msg);
            err.isFatalConfigError = true;
            throw err;
          }
          return response.json();
        })
        .then((configData) => {
          try {
            console.log("Config Data:", configData);
            const welcomeRaw = configData?.welcome_message || defaultWelcome;
            const welcomeMessage = configData?.site_config == true
              ? (typeof welcomeRaw === "string" && welcomeRaw.includes("href=")
                ? welcomeRaw.replace(/<a\s+(?:[^>]*?\s+)?href=/g, '<a style="color: blue;" href=')
                : welcomeRaw)
              : defaultWelcome;
            const chatbot_intro = configData?.site_config == true && configData?.chatbot_intro
              ? configData.chatbot_intro
              : chatbotIntro;
            const chatbot_disclaimer = configData?.site_config == true && configData?.disclaimer_message
              ? configData.disclaimer_message
              : chatbotDisclaimer;
            const enable_mic = configData?.enable_mic == "True";
            const show_full_language_text = configData?.show_full_language_text == "True";
            const auto_detect_language = configData?.auto_language_detection == "True";

            setMessages([{
              msg: welcomeMessage,
              category: null,
              fromUser: false,
              responseId: null,
              reaction: null
            }]);

            setChatbotIntro(chatbot_intro);
            setChatbotDisclaimer(chatbot_disclaimer);
            setEnableMic(enable_mic);
            setShowFullLanguageText(show_full_language_text);
            setAutoDetectLanguage(auto_detect_language);
          } catch (parseErr) {
            console.log("Config parse fallback:", parseErr.message);
            setMessages([{
              msg: defaultWelcome,
              category: null,
              fromUser: false,
              responseId: null,
              reaction: null
            }]);
          }

          return fetch(`${process.env.REACT_APP_SERVER_URL}/api/website/site=${websiteRef}`);
        })
        .then(async (response) => {
          if (!response.ok) {
            const msg = await extractErrorMessage(response);
            const err = new Error(msg);
            err.isFatalConfigError = true;
            throw err;
          }
          return response.json();
        })
        .then((data) => {
          try {
            if (data.banner_url === "") {
              setBanner(bg);
            } else if (data.banner_url) {
              setBanner(data.banner_url);
            }

            if (data.bot_name) {
              setBotName(data.bot_name);
            }

            if (data.categories && data.categories.length > 0 && data.categories.includes(',')) {
              let categoryList = data.categories.split(',');
              let categoryUuidList = (data.category_uuid || "").split(',');
              categoryList.forEach((obj, index) => {
                setCategories(oldData => [...oldData, { value: categoryUuidList[index], label: obj }]);
              });
            } else if (data.categories && data.categories.length > 0) {
              setCategories(oldData => [...oldData, { value: data.category_uuid, label: data.categories }]);
            }
          } catch (siteParseErr) {
            console.log("Site parse fallback:", siteParseErr.message);
            setBotName((prev) => prev || "Revize");
          }

          // Optional assets — never disable chat
          fetch(`${process.env.REACT_APP_SERVER_URL}/api/website/assets/get_icon_name/${websiteRef}?icon_type=CHATBOT_MESSAGE_ICON`)
            .then(async (response) => {
              if (!response.ok) return null;
              return response.json();
            })
            .then((iconName) => {
              if (!iconName) return null;
              return fetch(`${process.env.REACT_APP_SERVER_URL}/api/website/assets/icon/${iconName}`);
            })
            .then(async (response) => {
              if (response && response.ok) return response.json();
              return null;
            })
            .then((iconData) => {
              if (iconData && iconData.image) {
                setChatbotMessageIcon(iconData.image);
              }
            })
            .catch((iconErr) => {
              console.log(iconErr.message);
            });
        })
        .catch((err) => {
          console.log(err.message);
          // Only lock chat for config/site HTTP failures — not icons or parse issues
          if (err?.isFatalConfigError) {
            handleConfigLoadError(err.message);
          }
        });
    }
  }, []);

  const sendReaction = async (responseId, reactionValue) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const websiteRef = urlParams.get("websiteRef");

      // Build query parameters for the request
      const queryParams = new URLSearchParams({
        websiteRef,
        response_id: responseId.toString(),
        user_id: userId,
        reaction: reactionValue.toString()
      });

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/chat/reaction?${queryParams}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`HTTP ${response.status}: ${errorData.detail || "Unknown error"}`);
      }

      return true;
    } catch (error) {
      console.error("Reaction submission failed:", error);
      return false;
    }
  };

  const handleThumbsDown = async (message, index) => {
    // Check if this response was already rephrased
    const isRephrasedResponse = rephrasedResponseIds.has(message.responseId);

    // Send thumbs down reaction
    const success = await sendReaction(message.responseId, -1);
    if (success) {
      // Update the message with thumbs down reaction
      setMessages(prev => prev.map(m =>
        m.responseId === message.responseId
          ? { ...m, reaction: -1 }
          : m
      ));

      // Only show fallback message for the latest message
      if (index === messages.length - 1 && !message.fromUser) {
        if (isRephrasedResponse) {
          // This is a second thumbs down on a rephrased response
          setMessages(prev => [
            ...prev,
            {
              msg: "Please rephrase the prompt again and retry.",
              category: selectedCategory,
              fromUser: false,
              responseId: null,
              reaction: null,
              noReactionButtons: true // Flag to hide reaction buttons
            }
          ]);
        } else {
          // First thumbs down - offer to rephrase
          setMessages(prev => [
            ...prev,
            {
              msg: "Sorry for the inconvenience. Would you like me to rephrase and search again?",
              category: selectedCategory,
              fromUser: false,
              responseId: null,
              reaction: null,
              isRephrasePrompt: true
            }
          ]);

          // Store the original user message for rephrasing
          const userMessageIndex = messages.findIndex(m => m.responseId === message.responseId) - 1;
          if (userMessageIndex >= 0) {
            setWaitingForRephraseResponse(messages[userMessageIndex].msg);
          }
        }
      }
    }
  };

  const handleRephraseRequest = async (originalMessage) => {
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const websiteRef = urlParams.get("websiteRef");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/chat?websiteRef=${websiteRef}&message=${encodeURIComponent(originalMessage)}&session_id=${sessionId}&action=open&category=${selectedCategory}&user_id=${userId}&time_zone=${timeZone}&selected_language=${selectedLanguageRef.current}&reaction=0&rephrase=true`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      let message = data.response;
      message = message.replace(/^"|"$/g, '').replace(/\n/g, '<br>');
      message = message.replace(/\\"/g, '"');
      const formattedResponse = '<p>' + message + '</p>';

      // Mark this response ID as a rephrased response
      setRephrasedResponseIds(prev => new Set([...prev, data.response_id]));

      setMessages(prevMessages => [
        ...prevMessages,
        {
          msg: formattedResponse,
          category: selectedCategory,
          fromUser: false,
          responseId: data.response_id,
          reaction: null
        }
      ]);
    } catch (error) {
      setMessages(prevMessages => [
        ...prevMessages,
        {
          msg: "There is a temporary server downtime.",
          category: selectedCategory,
          fromUser: false,
          responseId: null,
          reaction: null
        }
      ]);
    } finally {
      setLoading(false);
      setWaitingForRephraseResponse(null);
    }
  };

  function createMarkup() {
    return { __html: '' };
  }

  useEffect(() => {
    if (configError) {
      return;
    }

    if (userInput !== '' && messages.length > 0 && messages[messages.length - 1]?.fromUser === true) {

      // Check if we're waiting for a rephrase response
      if (waitingForRephraseResponse) {
        const yesNoResponse = detectYesNo(userInput);

        if (yesNoResponse === 'yes') {
          // User wants to rephrase - call the rephrase API
          handleRephraseRequest(waitingForRephraseResponse);
          setUserInput('');
          return;
        } else if (yesNoResponse === 'no') {
          // User doesn't want to rephrase
          setMessages(prevMessages => [
            ...prevMessages,
            {
              msg: "I understand. Is there anything else I can help you with?",
              category: selectedCategory,
              fromUser: false,
              responseId: null,
              reaction: null
            }
          ]);
          setWaitingForRephraseResponse(null);
          setUserInput('');
          return;
        }
        // If it's neither yes nor no, continue with normal processing
        setWaitingForRephraseResponse(null);
      }

      setLoading(true);
      const urlParams = new URLSearchParams(window.location.search);
      let websiteRef = urlParams.get("websiteRef");

      const fetchData = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/api/chat?websiteRef=${websiteRef}&message=${encodeURIComponent(userInput)}&session_id=${sessionId}&action=open&category=${selectedCategory}&user_id=${userId}&time_zone=${timeZone}&selected_language=${selectedLanguageRef.current}&reaction=${0}`
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const data = await response.json();
          let message = data.response;
          message = message.replace(/^"|"$/g, '').replace(/\n/g, '<br>');
          message = message.replace(/\\"/g, '"');
          const formattedResponse = '<p>' + message + '</p>';

          setMessages(prevMessages => [
            ...prevMessages,
            {
              msg: formattedResponse,
              category: selectedCategory,
              fromUser: false,
              responseId: data.response_id,
              reaction: null
            }
          ]);
        } catch (error) {
          setMessages(prevMessages => [
            ...prevMessages,
            {
              msg: "There is a temporary server downtime.",
              category: selectedCategory,
              fromUser: false,
              responseId: null,
              reaction: null
            }
          ]);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
      setUserInput('');
    }

    if (bottomRef.current) {
      bottomRef.current.scrollTop = bottomRef.current.scrollHeight;
    }
  }, [messages, waitingForRephraseResponse]);

  useEffect(() => {
    const handleUnload = () => {
      const urlParams = new URLSearchParams(window.location.search);
      let websiteRef = urlParams.get("websiteRef");
      const endpoint = `${process.env.REACT_APP_SERVER_URL}/api/chat?websiteRef=${websiteRef}&message=&session_id=${sessionId}&action=close&category=${selectedCategory}`;
      navigator.sendBeacon(endpoint);
    };

    window.addEventListener('unload', handleUnload);
    return () => {
      window.removeEventListener('unload', handleUnload);
    };
  }, [sessionId, selectedCategory]);

  const sendMessage = (event) => {
    event.preventDefault();
    if (configError || loading || userInput.trim() === '') {
      return;
    }
    setMessages(prevMessages => [
      ...prevMessages,
      {
        msg: userInput,
        category: selectedCategory,
        fromUser: true,
        responseId: null,
        reaction: null
      }
    ]);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      setUserInput((previous) => previous + "\n");
      adjustTextareaHeight();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      sendMessage(event);
    } else if (event.key === 'Backspace') {
      adjustTextareaHeight();
    }
  };

  const [divHeight, setDivHeight] = useState(8);
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    const maxHeight = 70;
    textarea.style.height = "auto";

    if (textarea.scrollHeight <= maxHeight) {
      textarea.style.height = `${textarea.scrollHeight}px`;
      textarea.style.overflowY = "hidden";
      setDivHeight(8);
    } else {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = "auto";
      setDivHeight(10);
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [userInput]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMic = async () => {
    if (configError) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (isMicActive) {
      if (isUsingMediaRecorder && mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      } else if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsMicActive(false);
      return;
    }

    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = languageCodes[selectedLanguageRef.current] || 'en-US';

      let finalTranscript = '';

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        setUserInput(finalTranscript + interimTranscript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('SpeechRecognition error:', event.error);
        setIsMicActive(false);
        setMessages(prev => [
          ...prev,
          {
            msg: "Speech recognition failed. Please try again or type your message.",
            category: selectedCategory,
            fromUser: false,
            responseId: null,
            reaction: null
          }
        ]);
      };

      recognitionRef.current.onend = () => {
        if (isMicActive) {
          recognitionRef.current.start();
        } else {
          setIsMicActive(false);
        }
      };

      recognitionRef.current.start();
      setIsMicActive(true);
      setIsUsingMediaRecorder(false);
    }
    else {
      if (!window.isSecureContext) {
        setMessages(prev => [
          ...prev,
          {
            msg: "Speech to text requires HTTPS.",
            category: selectedCategory,
            fromUser: false,
            responseId: null,
            reaction: null
          }
        ]);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (e) => {
          audioChunksRef.current.push(e.data);
        };

        mediaRecorderRef.current.onstop = async () => {
          setUserInput("Transcribing...");
          const webmBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const wavBlob = await convertToWavBlob(webmBlob);

          const formData = new FormData();
          formData.append('audio', wavBlob, 'recording.wav');

          try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transcribe`, {
              method: 'POST',
              body: formData
            });
            const data = await response.json();
            setUserInput(data.text || "");
          } catch (err) {
            setMessages(prev => [
              ...prev,
              {
                msg: "Transcription failed: " + err.message,
                category: selectedCategory,
                fromUser: false,
                responseId: null,
                reaction: null
              }
            ]);
          }
          setIsMicActive(false);
        };

        mediaRecorderRef.current.start();
        setIsMicActive(true);
        setIsUsingMediaRecorder(true);
      } catch (err) {
        setMessages(prev => [
          ...prev,
          {
            msg: "Microphone access denied or unavailable.",
            category: selectedCategory,
            fromUser: false,
            responseId: null,
            reaction: null
          }
        ]);
      }
    }
  };

  const outerContainerClassName = layout === "2"
    ? "flex justify-center items-center w-full h-full shadow-lg"
    : "w-full h-full";

  const mainDivClassName = layout == 2 ?
    "flex flex-col h-5/6 w-3/12 border-2 border-black p-1 rounded-lg shadow-lg overflow-hidden" :
    "flex flex-col h-full ";

  useEffect(() => {
    if (chatbotMessageIcon) {
      const styleId = 'dynamic-bot-icon-style';
      let styleElement = document.getElementById(styleId);

      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }

      styleElement.textContent = `
      .bot-bubble .rounded-xl:before {
        content: "";
        background-image: url(${chatbotMessageIcon}) !important;
        font-size: 14px;
        display: block;
        position: absolute;
        left: -2rem;
        bottom: .75rem;
        width: 1.5rem;
        height: 1.5rem;
        background-size: 1.5rem;
        background-position: center center;
        background-repeat: no-repeat;
      }
    `;
    }
  }, [chatbotMessageIcon]);

  return (
    <div className={outerContainerClassName}>
      <div className={mainDivClassName} style={{ backgroundColor: themes.mainBgColor, backgroundSize: "cover" }}>
        <div style={{ backgroundColor: themes.primaryColor }} className="w-full sticky flex justify-center flex-col chat-header">
          <div style={{ color: themes.logoColor }} className='font-bold p-5 text-center'>{botName} AI Chatbot</div>
        </div>

        <div className={`h-full overscroll-y-auto ${layout === '2' ? 'relative flex flex-col' : ''}`}>
          {loading && (
            <div
              className='loading flex justify-center items-center w-full'
              style={{
                position: "absolute",
                height: "100%",
                pointerEvents: "none"
              }}
            >
            </div>
          )}

          {categories.length > 1 ? (
            <Select
              options={categories}
              onChange={handleTypeSelect}
              isDisabled={Boolean(configError)}
              value={categories.filter(function (category) {
                return category.value === selectedCategory;
              })}
              placeholder="Select Category"
            />
          ) : ''}
          <div className={`p-5 the-chat text-sm ${layout == "2" ? "flex-1 overflow-y-auto" : ""}`} ref={bottomRef} style={{ "whiteSpace": "pre-wrap", "overflowX": "hidden", "paddingBottom": "0" }}>
            <div
              className="px-5 pb-5 text-center"
              style={{ color: configError ? "#64748b" : "#737373" }}
              dangerouslySetInnerHTML={{ __html: chatbotIntro }}
            ></div>
            <div
              className={`rounded-xl mb-5 p-4 border flex gap-2.5 items-start text-sm leading-snug ${
                configError
                  ? "border-[#d0d9e3] bg-[#f4f7fa] text-[#0f2744]"
                  : "border-solid text-[#737373]"
              }`}
            >
              <svg className="flex-none mt-0.5" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.99994 12V7" stroke={configError ? "#0f2744" : "#737373"} strokeWidth="1.7"></path>
                <circle cx="1.1" cy="1.1" r="1.1" transform="matrix(1 0 0 -1 6.89993 6.2002)" fill={configError ? "#0f2744" : "#737373"} strokeWidth="0" stroke="none"></circle>
                <circle cx="8" cy="8" r="6.5" stroke={configError ? "#0f2744" : "#737373"} strokeWidth="1.75"></circle>
              </svg>
              <div dangerouslySetInnerHTML={{ __html: chatbotDisclaimer }}></div>
            </div>
            {!configError && (
            <ul className="message-block">
              {messages && messages.map((message, idx) => (
                <div key={idx} className={`mt-4 ${message.fromUser ? "user-bubble" : "text-left bot-bubble"}`}>
                  <div className="mt-4 p-4 rounded-xl"
                    style={{
                      backgroundColor: message.fromUser ? themes.blackColor : '#f2f2f2',
                      color: message.fromUser ? themes.primaryFontColor : themes.secondryFontColor
                    }}
                  >
                    <div className="break-words text-md" dangerouslySetInnerHTML={{ __html: message.msg }} />
                    <div className="break-words first-visible" dangerouslySetInnerHTML={createMarkup()} />
                    {/* Reaction buttons moved to be inline with AI chatbot label */}
                    {!message.fromUser && message.responseId && !message.noReactionButtons && (
                      <div className="flex items-center mt-4">
                        <span className="ai-chatbot-label" style={{ fontSize: '14px', opacity: 0.6 }}>
                          - AI chatbot
                        </span>
                        {message.reaction === 1 ? (
                          <button
                            className="p-1 opacity-50 cursor-not-allowed ml-2"
                            disabled
                          >
                            <ThumbUpIcon className="w-5 h-5 text-gray-500" />
                          </button>
                        ) : message.reaction === -1 ? (
                          <button
                            className="p-1 opacity-50 cursor-not-allowed ml-2"
                            disabled
                          >
                            <ThumbDownIcon className="w-5 h-5 text-gray-500" />
                          </button>
                        ) : (
                          <>
                            <button
                              className="p-1 rounded-full ml-2"
                              onClick={() => {
                                sendReaction(message.responseId, 1).then(success => {
                                  if (success) {
                                    setMessages(prev => prev.map(m =>
                                      m.responseId === message.responseId
                                        ? { ...m, reaction: 1 }
                                        : m
                                    ));
                                  }
                                });
                              }}
                            >
                              <ThumbUpIcon className="w-5 h-5 text-gray-500" />
                            </button>
                            <button
                              className="p-1 rounded-full ml-2"
                              onClick={() => handleThumbsDown(message, idx)}
                            >
                              <ThumbDownIcon className="w-5 h-5 text-gray-500" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </ul>
            )}
          </div>
        </div>

        <div className={`py-3 shadow-xl sticky w-full justify-center items-center flex sticky bottom-0 chat-bar z-10`}>
          <form
            className='w-full px-3 flex flex-col justify-center'
            onSubmit={sendMessage}
            onKeyDown={handleKeyPress}
            style={{ alignItems: 'flex-end' }}
          >
            {configError && (
              <div
                className="w-full mb-2 rounded-lg px-3 py-2.5 flex gap-2 items-start text-xs leading-snug"
                style={{
                  backgroundColor: "#f4f7fa",
                  border: "1px solid #d0d9e3",
                  color: "#0f2744",
                }}
              >
                <svg className="flex-none mt-0.5" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.99994 12V7" stroke="#0f2744" strokeWidth="1.7"></path>
                  <circle cx="1.1" cy="1.1" r="1.1" transform="matrix(1 0 0 -1 6.89993 6.2002)" fill="#0f2744"></circle>
                  <circle cx="8" cy="8" r="6.5" stroke="#0f2744" strokeWidth="1.75"></circle>
                </svg>
                <span>{configError}</span>
              </div>
            )}
            <div className={`flex w-full relative chat-input-wrap${configError ? " chat-input-wrap--disabled" : ""}`}>
              {chatbotMessageIcon ? (
                <img
                  className='sparkels absolute top-4 left-2 z-10'
                  src={chatbotMessageIcon}
                  alt="chatbot message icon"
                  style={{
                    width: '20px',
                    height: '20px',
                    marginBottom: '10px',
                    opacity: configError ? 0.35 : 1,
                    filter: configError ? "grayscale(1)" : "none",
                  }}
                />
              ) : (
                <img
                  className='w-8 sparkels absolute top-4 left-2 h-4 w-4 z-10'
                  src={sparkles}
                  alt="sparkles"
                  style={{
                    opacity: configError ? 0.35 : 1,
                    filter: configError ? "grayscale(1)" : "none",
                  }}
                />
              )}
              <textarea
                ref={textareaRef}
                className={`py-3 pl-10 w-full rounded-xl border-0 outline-none ${divHeight === 8 ? 'pr-32' : 'pr-28'} ${configError ? 'cursor-not-allowed' : ''}`}
                placeholder={configError ? "Chat unavailable" : languageQuestions[selectedLanguageRef.current]}
                id="message"
                name="message"
                value={userInput}
                disabled={Boolean(configError)}
                onChange={(e) => {
                  setUserInput(e.target.value);
                  adjustTextareaHeight();
                }}
                style={{
                  resize: "none",
                  height: "auto",
                  overflowY: "hidden",
                  flexGrow: 1,
                  minHeight: "46px",
                  lineHeight: "1.2",
                  background: configError ? "#f4f7fa" : undefined,
                  color: configError ? "#94a3b8" : undefined,
                }}
                rows={1}
                maxLength="1000"
              />
              <div className="absolute inset-y-0 right-10 flex items-center" style={{ zIndex: 20 }}>
                <div className="relative flex items-center gap-2">
                  {enableMic == true ? (
                    <button
                      type="button"
                      onClick={toggleMic}
                      disabled={Boolean(configError)}
                      className={`flex items-center justify-center px-2 text-sm rounded hover:bg-gray-100 border ${isMicActive ? 'animate-pulse bg-red-100' : ''} ${configError ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{
                        minWidth: "30px",
                        height: "30px",
                        borderColor: "#e5e7eb"
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                      </svg>
                    </button>
                  ) : null}
                  {autoDetectLanguage == false && (
                    <>
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        disabled={Boolean(configError)}
                        className={`flex items-center justify-center px-2 text-sm rounded hover:bg-gray-100 border ${configError ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{
                          minWidth: "20px",
                          height: "30px",
                          borderColor: "#e5e7eb"
                        }}
                      >
                        {showFullLanguageText == false
                          ? languageCodes[selectedLanguageRef.current]
                          : selectedLanguageRef.current || "en"}
                      </button>
                      {dropdownOpen && !configError && (
                        <div
                          ref={dropdownRef}
                          className="absolute bottom-full mb-1 right-0 bg-white border border-gray-200 rounded shadow-lg"
                          style={{
                            width: "160px",
                            maxHeight: "200px",
                            overflowY: "auto"
                          }}
                        >
                          {languages.map((lang) => (
                            <button
                              key={lang}
                              type="button"
                              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                              style={{
                                backgroundColor:
                                  selectedLanguageRef.current === lang ? "#f0f0f0" : "transparent",
                                fontWeight:
                                  selectedLanguageRef.current === lang ? "600" : "normal",
                              }}
                              onClick={() => {
                                selectedLanguageRef.current = lang;
                                setDropdownOpen(false);
                              }}
                            >
                              {lang} ({languageCodes[lang] || ""})
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {dropdownOpen && !configError && (
                    <div
                      ref={dropdownRef}
                      className="absolute bottom-full mb-1 right-0 bg-white border border-gray-200 rounded shadow-lg"
                      style={{
                        width: "160px",
                        maxHeight: "200px",
                        overflowY: "auto"
                      }}
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang}
                          type="button"
                          className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                          style={{
                            backgroundColor: selectedLanguageRef.current === lang ? "#f0f0f0" : "transparent",
                            fontWeight: selectedLanguageRef.current === lang ? "600" : "normal"
                          }}
                          onClick={() => {
                            selectedLanguageRef.current = lang;
                            setDropdownOpen(false);
                          }}
                        >
                          {lang} ({languageCodes[lang] || ""})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="submit"
                style={{
                  opacity: loading || configError ? 0.5 : 1,
                  cursor: loading || configError ? 'not-allowed' : 'pointer'
                }}
                className="absolute inset-y-0 right-4 flex items-center justify-center h-full w-4"
                disabled={loading || Boolean(configError)}
              >
                <img className='w-4 h-4' src={arrow} alt="arrow" />
              </button>
            </div>
          </form>
        </div>

        {/* ===== FOOTER LINE ===== */}
        <div className="text-center text-xs text-gray-400 py-1">
  Powered by{" "}
  <a
    href="https://www.revize.com"
    target="_blank"
    rel="noopener noreferrer"
    className="hover:text-gray-600 underline"
  >
    revize.AI
  </a>
</div>

      </div>
    </div>
  );
}

export default Chatbot;